import { defineStore } from 'pinia'

export interface PassageSelectionState {
  selectedItems: string[] // 선택된 지문 ID 배열 (예: ['passage_123', 'passage_456'])
  selectedFolders: string[] // 전체 선택된 폴더 ID 배열 (예: ['1', '2'])
  folderPassagesCache: Record<string, string[]> // 폴더별 지문 ID 캐시
  isAllSelected: boolean // 전체 선택 상태 추가
  selectionOrder: string[] // 선택 순서를 유지하는 배열 (첫 선택부터 순서대로)
}

export const usePassageSelectionStore = defineStore('passageSelection', {
  state: (): PassageSelectionState => ({
    selectedItems: [],
    selectedFolders: [],
    folderPassagesCache: {},
    isAllSelected: false,
    selectionOrder: [],
  }),

  getters: {
    // 선택된 항목 개수
    selectedCount: (state) => state.selectedItems.length,

    // 특정 항목이 선택되었는지 확인
    isItemSelected: (state) => (itemId: string) =>
      state.selectedItems.includes(itemId),

    // 특정 폴더가 전체 선택되었는지 확인
    isFolderFullySelected: (state) => (folderId: string) =>
      state.selectedFolders.includes(folderId),

    // 폴더의 선택 상태 계산
    getFolderSelectionState: (state) => (folderId: string) => {
      const folderPassageIds = state.folderPassagesCache[folderId] || []
      if (folderPassageIds.length === 0) return 'none'

      const selectedCount = folderPassageIds.filter((id) =>
        state.selectedItems.includes(id),
      ).length

      if (selectedCount === 0) return 'none'
      if (selectedCount === folderPassageIds.length) return 'selected'
      return 'partial'
    },

    // 특정 아이템의 선택 순서 번호 반환 (1-based index)
    getItemSelectionIndex: (state) => (itemId: string) => {
      const index = state.selectionOrder.indexOf(itemId)
      return index === -1 ? 0 : index + 1
    },
  },

  actions: {
    setAllSelected(value: boolean) {
      this.isAllSelected = value
    },

    // 항목 선택/해제 토글
    toggleItem(itemId: string) {
      const index = this.selectedItems.indexOf(itemId)
      if (index > -1) {
        // 선택 해제: selectedItems와 selectionOrder에서 모두 제거
        this.selectedItems.splice(index, 1)
        const orderIndex = this.selectionOrder.indexOf(itemId)
        if (orderIndex > -1) {
          this.selectionOrder.splice(orderIndex, 1)
        }
      } else {
        // 선택: selectedItems와 selectionOrder에 추가
        this.selectedItems.push(itemId)
        this.selectionOrder.push(itemId)
      }
    },

    // 여러 항목 추가
    addItems(itemIds: string[]) {
      const newItems = itemIds.filter((id) => !this.selectedItems.includes(id))
      this.selectedItems.push(...newItems)
      // 선택 순서에도 추가 (순서 유지) - 중복 제거
      const newOrderItems = itemIds.filter(
        (id) => !this.selectionOrder.includes(id),
      )
      this.selectionOrder.push(...newOrderItems)
    },

    // 여러 항목 제거
    removeItems(itemIds: string[]) {
      this.selectedItems = this.selectedItems.filter(
        (id) => !itemIds.includes(id),
      )
      // 선택 순서에서도 제거
      this.selectionOrder = this.selectionOrder.filter(
        (id) => !itemIds.includes(id),
      )
    },

    // 폴더 전체 선택
    selectFolder(folderId: string, passageIds: string[]) {
      // 폴더의 지문들을 캐시에 저장
      this.folderPassagesCache[folderId] = passageIds

      // 지문들을 선택 목록에 추가
      this.addItems(passageIds)

      // 폴더를 전체 선택 목록에 추가
      if (!this.selectedFolders.includes(folderId)) {
        this.selectedFolders.push(folderId)
      }
    },

    // 폴더 전체 선택 해제
    deselectFolder(folderId: string) {
      const folderPassageIds = this.folderPassagesCache[folderId] || []

      // 폴더의 모든 지문을 선택 해제
      this.removeItems(folderPassageIds)

      // 폴더를 전체 선택 목록에서 제거
      this.selectedFolders = this.selectedFolders.filter(
        (id) => id !== folderId,
      )
    },

    // 폴더 캐시 업데이트
    updateFolderCache(folderId: string, passageIds: string[]) {
      this.folderPassagesCache[folderId] = passageIds
    },

    // 선택 상태 초기화
    clearSelection() {
      this.selectedItems = []
      this.selectedFolders = []
      this.selectionOrder = []
      // 캐시는 유지 (성능 최적화)
    },

    // 전체 초기화 (캐시 포함)
    resetAll() {
      this.selectedItems = []
      this.selectedFolders = []
      this.folderPassagesCache = {}
      this.isAllSelected = false
      this.selectionOrder = []
    },

    // 폴더 선택 상태 업데이트 (일부 선택 시)
    updateFolderSelectionState(folderId: string) {
      const folderPassageIds = this.folderPassagesCache[folderId] || []
      if (folderPassageIds.length === 0) return

      const selectedCount = folderPassageIds.filter((id) =>
        this.selectedItems.includes(id),
      ).length

      if (
        selectedCount === folderPassageIds.length &&
        folderPassageIds.length > 0
      ) {
        // 모든 지문이 선택된 경우 폴더를 전체 선택 목록에 추가
        if (!this.selectedFolders.includes(folderId)) {
          this.selectedFolders.push(folderId)
        }
      } else {
        // 일부만 선택되거나 선택 해제된 경우 폴더를 전체 선택 목록에서 제거
        this.selectedFolders = this.selectedFolders.filter(
          (id) => id !== folderId,
        )
      }
    },
  },

  // persist 제거: 선택 상태는 팝업 생명주기 내에서만 유지
  // 페이지별 독립적인 선택 상태를 위해 sessionStorage 사용 안 함
})
